import type { RefdataAcceptanceSourceBinding } from '../../types/refdata';
/**
 * Dual-use refdata client — the ONLY module providers should use to reach
 * the nationwide reference-data store.
 *
 *   Browser        → authedFetch('/api/refdata/…') (src/server/refdataRouter.ts)
 *   Node (server / Cloud Run job) → the query engine directly, no HTTP hop.
 *
 * BUNDLE SAFETY (copies src/services/dam/buildDamInline.ts:91-133): the
 * server engine is first-party code under src/server/, so a static import
 * here would drag @google-cloud/storage + geotiff into the CLIENT chunk.
 * It is therefore loaded via a runtime-computed specifier (`importByPath`),
 * which Vite cannot statically analyse. But that same trick defeats esbuild
 * in the Cloud Run JOB bundles (the source tree does not exist under
 * /app/dist), so the server entrypoints (server.ts,
 * scripts/runSedBootstrapJob.ts, scripts/runReportGenerationJob.ts)
 * STATICALLY import the engine and inject it via setRefdataServerEngine()
 * at startup. When injected, importByPath is never reached.
 *
 * Result semantics (same as the engine):
 *   null  → dataset unavailable → caller falls back to its live API path
 *   empty → authoritative empty (national snapshot has nothing there)
 *
 * Providers should wrap calls in tryRefdata(), which never throws and
 * negative-caches unavailable datasets for 60 s.
 */

import type {
  RefdataBbox,
  RefdataBboxResult,
  RefdataContainsResult,
  RefdataDatasetId,
  RefdataDatasetStatus,
  RefdataElevationResult,
  RefdataIntersectResult,
  RefdataLatLng,
  RefdataNearResult,
  RefdataPropFilter,
} from '../../types/refdata';
import type { RefdataEngine, RefdataIntersectOptions } from '../../server/refdata/queryEngine';
import { authedFetch } from '../../lib/authedFetch';

export type { RefdataIntersectOptions } from '../../server/refdata/queryEngine';

function isServerContext(): boolean {
  return typeof window === 'undefined';
}

/** Dynamic import via a runtime-computed path — defeats Vite's static
 *  analysis so server-only deps never enter the client bundle.
 *  `@vite-ignore` silences the "will not be analyzed" warning. */
async function importByPath(relPath: string): Promise<Record<string, unknown>> {
  const spec = relPath; // variable on purpose — defeats static analysis
  return import(/* @vite-ignore */ spec) as Promise<Record<string, unknown>>;
}

let injectedEngine: RefdataEngine | null = null;
let privateAcceptance: { binding: RefdataAcceptanceSourceBinding; datasets: ReadonlySet<RefdataDatasetId> } | null = null;

export function isPrivateRefdataAcceptanceDataset(id: RefdataDatasetId): boolean { return privateAcceptance?.datasets.has(id) ?? false; }
export function getPrivateRefdataAcceptanceBinding(): RefdataAcceptanceSourceBinding | null {
  return privateAcceptance ? { ...privateAcceptance.binding, versions: { ...privateAcceptance.binding.versions }, manifestHashes: { ...privateAcceptance.binding.manifestHashes } } : null;
}

/** Inject the statically-bundled engine (called once by server.ts and each
 *  Cloud Run job entry). Ordinary injection is replaceable; a private frozen
 *  acceptance snapshot cannot be replaced by the default singleton. */
export function setRefdataServerEngine(engine: RefdataEngine, acceptance?: { digest: string; datasets: readonly RefdataDatasetId[]; versions: Partial<Record<RefdataDatasetId, string>>; manifestHashes: Partial<Record<RefdataDatasetId, string>> }): void {
  if (acceptance && (!isServerContext() || !/^[a-f0-9]{64}$/.test(acceptance.digest) || acceptance.datasets.some(id => !acceptance.versions[id] || !/^[a-f0-9]{64}$/.test(acceptance.manifestHashes[id] ?? '')))) throw new Error('Invalid private refdata acceptance identity');
  if (privateAcceptance && privateAcceptance.binding.digest !== acceptance?.digest) throw new Error('Cannot overwrite a private acceptance refdata engine');
  privateAcceptance = acceptance ? { binding: { schemaVersion: 1, purpose: 'private-refdata-acceptance', digest: acceptance.digest, versions: { ...acceptance.versions }, manifestHashes: { ...acceptance.manifestHashes } }, datasets: new Set(acceptance.datasets) } : null;
  unavailableUntil.clear();
  injectedEngine = engine;
}

let enginePromise: Promise<RefdataEngine | null> | null = null;

async function resolveServerEngine(): Promise<RefdataEngine | null> {
  if (injectedEngine) return injectedEngine;
  if (!enginePromise) {
    enginePromise = importByPath('../../server/refdata/queryEngine')
      .then(mod => (mod as { getRefdataEngine: () => RefdataEngine }).getRefdataEngine())
      .catch((err: unknown) => {
        console.warn('[refdata] server engine unavailable (no injection, dynamic import failed):',
          err instanceof Error ? err.message : err);
        return null;
      });
  }
  return enginePromise;
}

async function apiGet<T>(path: string, arrayField: string): Promise<T | null> {
  const res = await authedFetch(path);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`refdata API ${res.status} for ${path.split('?')[0]}`);
  return validatePayload(await res.json() as T, arrayField, path);
}

async function apiPost<T>(path: string, body: unknown, arrayField: string): Promise<T | null> {
  const res = await authedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`refdata API ${res.status} for ${path}`);
  return validatePayload(await res.json() as T, arrayField, path);
}

/** Wire-shape guard: a 200 whose body lacks the expected array (an HTML
 *  error page behind a proxy, a stubbed fetch in tests, …) must throw so
 *  tryRefdata degrades to the live path instead of crashing a consumer. */
function validatePayload<T>(payload: T, arrayField: string, path: string): T {
  if (payload == null || !Array.isArray((payload as Record<string, unknown>)[arrayField])) {
    throw new Error(`refdata API returned a malformed payload (missing ${arrayField}[]) for ${path.split('?')[0]}`);
  }
  return payload;
}

// ---------------------------------------------------------------------------
// Primitives — thin, throwing; providers wrap these in tryRefdata().
// ---------------------------------------------------------------------------

export async function refdataNear(
  datasetId: RefdataDatasetId,
  point: RefdataLatLng,
  radiusKm: number,
  limit: number,
  filter?: RefdataPropFilter,
): Promise<RefdataNearResult | null> {
  if (isServerContext()) {
    const engine = await resolveServerEngine();
    return engine ? engine.near(datasetId, point, radiusKm, limit, filter) : null;
  }
  const params = new URLSearchParams({
    lat: String(point.lat),
    lng: String(point.lng),
    radiusKm: String(radiusKm),
    limit: String(limit),
  });
  if (filter) params.set('filter', JSON.stringify(filter));
  return apiGet(`/api/refdata/${datasetId}/near?${params.toString()}`, 'hits');
}

export async function refdataBbox(
  datasetId: RefdataDatasetId,
  bbox: RefdataBbox,
  limit: number,
): Promise<RefdataBboxResult | null> {
  if (isServerContext()) {
    const engine = await resolveServerEngine();
    return engine ? engine.bbox(datasetId, bbox, limit) : null;
  }
  const params = new URLSearchParams({
    minLat: String(bbox.minLat),
    minLng: String(bbox.minLng),
    maxLat: String(bbox.maxLat),
    maxLng: String(bbox.maxLng),
    limit: String(limit),
  });
  return apiGet(`/api/refdata/${datasetId}/bbox?${params.toString()}`, 'features');
}

export async function refdataIntersect(
  datasetId: RefdataDatasetId,
  polygon: RefdataLatLng[],
  opts?: RefdataIntersectOptions,
): Promise<RefdataIntersectResult | null> {
  if (isServerContext()) {
    const engine = await resolveServerEngine();
    return engine ? engine.intersect(datasetId, polygon, opts) : null;
  }
  return apiPost(`/api/refdata/${datasetId}/intersect`, {
    polygon,
    bufferM: opts?.bufferM,
    wantAcreage: opts?.wantAcreage,
    limit: opts?.limit,
  }, 'hits');
}

export async function refdataContains(
  datasetId: RefdataDatasetId,
  point: RefdataLatLng,
): Promise<RefdataContainsResult | null> {
  if (isServerContext()) {
    const engine = await resolveServerEngine();
    return engine ? engine.contains(datasetId, point) : null;
  }
  const params = new URLSearchParams({ lat: String(point.lat), lng: String(point.lng) });
  return apiGet(`/api/refdata/${datasetId}/contains?${params.toString()}`, 'features');
}

export async function refdataElevationPoints(
  points: RefdataLatLng[],
): Promise<RefdataElevationResult | null> {
  if (isServerContext()) {
    const engine = await resolveServerEngine();
    return engine ? engine.elevationPoints(points) : null;
  }
  return apiPost('/api/refdata/elevation/points', { points }, 'elevations');
}

export interface RefdataStatusSummary {
  enabled: boolean;
  datasets: RefdataDatasetStatus[];
}

export async function refdataStatus(): Promise<RefdataStatusSummary | null> {
  if (isServerContext()) {
    const engine = await resolveServerEngine();
    if (!engine) return null;
    const datasets = await engine.status();
    return { enabled: datasets.some(d => d.available), datasets };
  }
  return apiGet('/api/refdata/status', 'datasets');
}

// ---------------------------------------------------------------------------
// tryRefdata — the provider-facing guard. Never throws; converts every
// failure into "use the live API instead" and negative-caches unavailable
// datasets so a cold store costs one probe per dataset per minute.
// ---------------------------------------------------------------------------

const UNAVAILABLE_TTL_MS = 60 * 1000;
const unavailableUntil = new Map<string, number>();

export async function tryRefdata<T>(
  datasetId: RefdataDatasetId,
  fn: () => Promise<T | null>,
): Promise<T | null> {
  const now = Date.now();
  const blockedUntil = unavailableUntil.get(datasetId);
  if (blockedUntil != null && blockedUntil > now) return null;
  try {
    const result = await fn();
    if (result == null) {
      if (privateAcceptance?.datasets.has(datasetId)) throw new Error(`Private acceptance dataset unavailable: ${datasetId}`);
      unavailableUntil.set(datasetId, now + UNAVAILABLE_TTL_MS);
      return null;
    }
    return result;
  } catch (err) {
    if (privateAcceptance?.datasets.has(datasetId)) throw new Error(`Private acceptance query failed for ${datasetId}; live fallback is withheld`, { cause: err });
    console.warn(`[refdata:${datasetId}] falling back to live API:`,
      err instanceof Error ? err.message : err);
    unavailableUntil.set(datasetId, now + UNAVAILABLE_TTL_MS);
    return null;
  }
}

/** Test-only: clear the negative cache and any resolved engine. */
export function resetRefdataClientForTests(): void {
  unavailableUntil.clear();
  enginePromise = null;
  injectedEngine = null;
  privateAcceptance = null;
}
